/**
 * Authors: Koen, mariushirjanu, Triadi
 * Created: 07/11/2018
 * Upgraded: 28/10/2022
 */
import * as _ from 'underscore';
import { util } from '../util';
import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, SimpleChange, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { GaWizard, IWizardButton, WizardEvent } from './ga-wizard';
import { BodyClassService } from '../services/body-class.service';
import { FormattingService } from '../services/formatting.service';
import { GaEventService, IGAEvent } from '../services/ga-event.service';
import { Subscription } from 'rxjs';
import { GaWizardService } from './ga-wizard.service';
import { TranslationService } from '../translation/translation.service';

export interface ITranslateText {
  translateText?:string;
  translateValues?:{ [ property:string ]:string };
}

@Component({
  selector: 'wizard-wrapper',
  template: `
    <div #wizardWrapper>
      <div *ngIf="!showAsDialog">
        <div *ngIf="error" class="alert alert-danger wizard-error">
          <strong>{{ error }}</strong>
        </div>

        <div *ngIf="!error">
          <div *ngIf="loading">
            <ng-container *ngTemplateOutlet="loadingIndicator"></ng-container>
          </div>
          <div *ngIf="denied" translate>
            APP.WIZARD.NOT_ALLOWED
          </div>
          <div class="wizard" [class.has-header]="!!wizardHeaderExtraRef.children.length" *ngIf="!loading && !denied">
            <fixed-to-top [isInDialog]="isInDialog">
              <div class="navbar-wizard clearfix" *ngIf="!hideHeader">
                <div class="clearfix">
                  <wizard-controls *ngIf="showHeaderControls" [navigationDisabled]="navigationDisabled" [completedSteps]="completedSteps" [wizardId]="wizardId"></wizard-controls>
                  <div class="float-end wizard-actions">
                    <ng-content select=".wizard-actions-before"></ng-content>
                    <ng-container *ngTemplateOutlet="buttonList"></ng-container>
                    <ng-content select=".wizard-actions-after"></ng-content>
                  </div>
                  <h1 class="wizard-title">
                    <span>{{ title }}</span>
                    <span class="wizard-title-right">{{ extraTitle | translate }}</span>
                  </h1>
                  <div *ngIf="showProgress && (progress || progress === 0)" class="progress wizard-progress" [ngClass]="{'wizard-progress-0': progress === 0}">
                    <div class="progress-bar" role="progressbar" [ngStyle]="{width: (progress > 0 ? progress : '20') + '%'}" [attr.aria-valuenow]="progress" [attr.aria-valuemin]="0" [attr.aria-valuemin]="100"><span translate [translateParams]="{percentage: progress}">GA.GENERAL.PROGRESS</span></div>
                  </div>
                </div>
              </div>

              <div #wizardHeaderExtraRef>
                <ng-content select=".wizard-header-extra" class="col-sm-4"></ng-content>
              </div>
            </fixed-to-top>
            <wizard-content [wizardId]="wizardId" [isInDialog]="isInDialog"></wizard-content>
          </div>
        </div>
      </div>

      <div *ngIf="showAsDialog" class="wizard-as-dialog">
        <div class="message-dialog">
          <div class="modal-header">
            <h4 class="modal-title" translate><span>{{ title }}</span></h4>
            <wizard-controls *ngIf="showHeaderControls" [navigationDisabled]="navigationDisabled" [completedSteps]="completedSteps" [wizardId]="wizardId"></wizard-controls>
          </div>

          <div class="modal-body">
            <div *ngIf="loading">
              <ng-container *ngTemplateOutlet="loadingIndicator"></ng-container>
            </div>
            <div *ngIf="error" class="alert alert-danger wizard-error">
              <strong>{{ error }}</strong>
            </div>
            <div *ngIf="!loading && !error" class="modal-content-container no-padding">
              <wizard-content [wizardId]="wizardId" [isInDialog]="true"></wizard-content>
            </div>
          </div>

          <div class="modal-footer">
            <ng-container *ngTemplateOutlet="buttonList"></ng-container>
          </div>
        </div>
      </div>
      
      <ng-template #loadingIndicator>
        <h2><activity-indicator [toLoad]="loadingValues?.toload" [useDefaultText]="!!(loadingValues?.toload)"></activity-indicator></h2>
      </ng-template>

      <ng-template #buttonList>
        <ng-container *ngFor="let action of actions; let idx = index; trackById">
          <!-- regular wizard buttons -->
          <ng-container *ngIf="!action.buttons?.length; else dropdownButton">
            <button
              class="btn"
              [ngClass]="action.classes"
              *ngIf="!action.hidden"
              [busyButton]="action.busy"
              [bbIconMargin]="true"
              [bbPreventDisable]="action.preventDisable"
              [disabled]="action.disabled"
              (click)="action.click()">
              <i *ngIf="action.iconVisible && action.iconClasses" class="fa" [ngClass]="action.iconClasses"></i>
              {{ action.label }}
            </button>
          </ng-container>

          <!-- dropdown wizard buttons -->
          <ng-template #dropdownButton>
            <ng-container *ngIf="!action.hidden">
              <div class="btn-group wizard-action-dropdown" dropdown placement="bottom right">
                <button class="btn dropdown-toggle" [ngClass]="action.classes" [busyButton]="action.busy" [bbIconMargin]="true" [bbPreventDisable]="action.preventDisable" [disabled]="action.disabled" [id]="'wizard-action-dropdown-' + idx" dropdownToggle>
                  <span class="margin-right-sm">{{ action.label }}</span>
                  <i class="fa" *ngIf="action.iconVisible" [ngClass]="action.iconClasses"></i>
                </button>
                
                <ul class="dropdown-menu dropdown-menu-right" *dropdownMenu [attr.aria-labelledby]="'wizard-action-dropdown-' + idx" role="menu">
                  <li *ngFor="let subAction of action.buttons">
                    <a href="javascript:void(0)" (click)="subAction.click()">
                      <i *ngIf="subAction.iconVisible && subAction.iconClasses" class="fa margin-right-xs" [ngClass]="subAction.iconClasses"></i>
                      {{ subAction.label }}
                    </a>
                  </li>
                </ul>
              </div>
            </ng-container>
          </ng-template>
        </ng-container>
      </ng-template>
    </div>
  `
})
export class WizardWrapperComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('wizardWrapper', { static: true }) public wizardWrapper:ElementRef<HTMLDivElement>;

  @Input() public wizardTitle:string | ITranslateText;
  @Input() public extraTitle:string;
  @Input() public error:string;
  @Input() public loading:string | ITranslateText;
  @Input() public navigationDisabled:boolean;
  @Input() public hideHeader:boolean;
  @Input() public progress?:number;
  @Input() public showProgress?:boolean;
  @Input() public completedSteps?:string[];
  @Input() public wizardId:string;
  @Input() public isInDialog?:boolean;
  @Input() public showAsDialog?:boolean; // this is the new way of displaying the wizard as a dialog, should replace `isInDialog`

  public denied:boolean;
  public showHeaderControls:boolean;
  public actions:IWizardButton[];
  public loadingValues:{ [ property:string ]:string };
  public title:string;
  private wizard:GaWizard;
  private eventHandler:Subscription;
  private wizardWrapperElement:JQuery;

  constructor(
    private bodyClassService:BodyClassService,
    private formattingService:FormattingService,
    private gaEventService:GaEventService,
    private translationService:TranslationService,
    private wizardService:GaWizardService,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    /***/
  }

  public ngOnInit():void {
    // if wizard id is provided then isolate instance of wizard services
    this.wizard = this.wizardService.getInstance(this.wizardId);
    this.wizard.title = <string>this.wizardTitle;
    this.wizardWrapperElement = $(this.wizardWrapper.nativeElement);

    if (!this.hideHeader) {
      this.bodyClassService.add('wizard-header');
    }

    if(util.isNullOrUndefined(this.isInDialog)){
      this.isInDialog = this.detectIsInDialog();
    }

    this.actions = this.wizard.actions();
    this.updateHeaderVisibility();

    this.listen();
    this.changeDetectorRef.detectChanges();
  }

  private listen = () => {
    this.eventHandler = this.gaEventService.events.subscribe((event:IGAEvent) => {
      if (event.name === `${WizardEvent.ACTIONS_CHANGED}.${this.wizardId}`) {
          this.actions = event.data;
        this.changeDetectorRef.detectChanges();
      }

      if (event.name === `${WizardEvent.STEPS_CHANGED}.${this.wizardId}`) {
        this.updateHeaderVisibility();
        this.changeDetectorRef.detectChanges();
      }

      if (event.name === `${WizardEvent.TITLE_CHANGED}.${this.wizardId}`) {
        this.updateWizardTitle(event.data);
        this.changeDetectorRef.detectChanges();
      }
    });
  };

  private updateHeaderVisibility = () => {
    this.showHeaderControls = this.wizard.visibleSteps().length > 1;
  };

  public ngOnChanges(changes:SimpleChanges):void {
    // get translation key for the loading text
    if (changes.loading) {
      this.loading = this.extractTranslateText(changes.loading);
      this.loadingValues = this.extractTranslateValues(changes.loading);
    }

    // get translation key for the wizard title
    if (changes.wizardTitle) {
      this.wizardTitle = changes.wizardTitle.currentValue;
      this.updateWizardTitle();
    }

    if (changes.navigationDisabled) {
      this.navigationDisabled = changes.navigationDisabled.currentValue;
    }

    if (changes.progress) {
      this.progress = Number(this.formattingService.formatNumber(changes.progress.currentValue, '##0'));
    }

    if (changes.showProgress) {
      this.showProgress = changes.showProgress.currentValue;
    }

    if (changes.completedSteps) {
      this.completedSteps = _.clone(changes.completedSteps.currentValue);
    }
  }

  private extractTranslateText(translationObj:SimpleChange):string {
    if (translationObj.currentValue?.translateText) {
      return translationObj.currentValue.translateText;
    }

    return translationObj.currentValue;
  }

  private extractTranslateValues(translationObj:SimpleChange):{ [ property:string ]:string } {
    if (translationObj.currentValue) {
      return translationObj.currentValue.translateValues;
    }

    return {};
  }

  private updateWizardTitle(title?:string|ITranslateText):void {
    title = title || this.wizardTitle;
    if((_.isObject(title))){
      this.title = this.translationService.instant((<ITranslateText>title).translateText, (<ITranslateText>title).translateValues);
    } else {
      this.title = this.translationService.instant(<string>title);
    }
  }

  private detectIsInDialog():boolean {
    return !!$('modal-container').find(this.wizardWrapperElement).length;
  }

  public ngOnDestroy():void {
    this.bodyClassService.remove('wizard-header');
    this.eventHandler.unsubscribe();
  }
}